import { useSelector } from "react-redux";
import moment from "moment";
import Image from "../../components/general/Image";
import { ProfilePicDecorator, DummyProfile as DummyProfilePic, PriceTag } from "../../constants/ImagePaths";
import Button from "../../components/general/Button";
import DashedChips from "../../components/common/DashedChips";
import { Verified } from "../../constants/ImagePaths";
import useDocumentTitle from "../../hooks/useDocumentTitle";
import { ArrowUpRight } from "../../components/common/Icons";

const DeveloperProfile = ({ onUnlockRatesClick }) => {
    const { developer: { detail } } = useSelector(state => state.common);
    useDocumentTitle(detail?.first_name || "Developer Profile");    

    return (
        <div className="bg-[#292929] text-white pb-[100px]">
            {
                detail?.display_pay_range ? (
                    <div className="relative">
                        <Image className="absolute top-[20px] w-[95px] h-[50px] right-0" src={PriceTag} alt="price-tag" />

                        <div className="relative z-1 top-[20px] pr-[10px] flex flex-col justify-end items-end">
                            <span className="font-bold text-[18px] text-black"> ${detail.display_pay_range} </span>
                            <span className="block">Per Hour</span>
                        </div>
                    </div>
                )
                    :
                    <div className="pb-[50px]" />
            }

            <div className="px-[20px] lg:px-[40px] -mt-[20px]">
                <div className="profileImg">
                    <div>
                        <span className="logo-block"><Image className="translate-x-[-10px] w-[20px]" src={ProfilePicDecorator} alt="logo-block" /></span>
                        <Image className="w-[100px] h-[100px] rounded-[6px]" src={detail.profile_picture?.file || DummyProfilePic} alt="profile-pic" lazyLoad={false} />
                    </div>
                </div>
                <div className="text-[22px] lg:text-[30px] font-semibold flex items-center gap-[10px] justify-start mt-[10px]">
                    <span>{detail.first_name}</span>
                    {
                        detail.country && detail.country.flag && (
                            <Image className="w-[30px] h-[20px]" src={detail.country.flag.file} alt="" />
                        )
                    }
                </div>

                {
                    detail.country && detail.country.name && (
                        <p className="text-[#A0A0A0] font-extralight text-[12px] lg:text-[0.8rem]">From {detail.country?.name}</p>
                    )
                }
                <div className="flex items-center gap-2 mt-1">
                    {/* Green hex badge with checkmark and tooltip */}
                    <span className="inline-flex items-center relative group cursor-pointer">
                        <div className="flex items-center gap-1">
                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="size-5 text-green-600">
                                <path fillRule="evenodd" d="M8.603 3.799A4.49 4.49 0 0 1 12 2.25c1.357 0 2.573.6 3.397 1.549a4.49 4.49 0 0 1 3.498 1.307 4.491 4.491 0 0 1 1.307 3.497A4.49 4.49 0 0 1 21.75 12a4.49 4.49 0 0 1-1.549 3.397 4.491 4.491 0 0 1-1.307 3.497 4.491 4.491 0 0 1-3.497 1.307A4.49 4.49 0 0 1 12 21.75a4.49 4.49 0 0 1-3.397-1.549 4.49 4.49 0 0 1-3.498-1.306 4.491 4.491 0 0 1-1.307-3.498A4.49 4.49 0 0 1 2.25 12c0-1.357.6-2.573 1.549-3.397a4.49 4.49 0 0 1 1.307-3.497 4.49 4.49 0 0 1 3.497-1.307Zm7.007 6.387a.75.75 0 1 0-1.22-.872l-3.236 4.53L9.53 12.22a.75.75 0 0 0-1.06 1.06l2.25 2.25a.75.75 0 0 0 1.14-.094l3.75-5.25Z" clipRule="evenodd" />
                            </svg>
                            <span className="text-[12px] font-light">Verified Expert</span>
                        </div>


                        {/* Tooltip */}
                        <span className="absolute left-1/2 -translate-x-1/2 bottom-full mb-2 w-max max-w-[200px] px-3 py-2 rounded bg-black text-white text-xs opacity-0 group-hover:opacity-100 pointer-events-none transition-opacity z-20 shadow-lg whitespace-normal break-words text-center">
                            Verified DevHire Developer
                        </span>
                    </span>
                </div>
                <p className="text-[16px] lg:text-[20px] font-semibold mt-[5px]">{detail?.specialization?.name}</p>

                {
                    detail.display_pay_range ?
                        <Button className="bg-primary-color rounded-[6px] text-black h-[40px] mt-[40px] px-[10px]" onClick={onUnlockRatesClick}>
                            <div className="flex items-center justify-center gap-2">
                                <span>Hire {detail?.first_name} now</span>
                                <ArrowUpRight className="w-4 h-4" />
                            </div>
                        </Button>
                        :
                        <Button className="bg-primary-color rounded-[6px] text-black h-[40px] mt-[40px] px-[10px] md:w-[250px]" onClick={onUnlockRatesClick}>
                            <div className="flex items-center justify-center gap-2">
                                <span>Unlock Rates & Hire Now</span>
                                <ArrowUpRight className="w-4 h-4" />
                            </div>
                        </Button>
                }

                <div className="text-[16px] font-semibold mt-[10px]">{detail.total_experience} years of commercial experience</div>

                <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-3 gap-[10px] mt-[20px]">
                    {
                        detail?.tags && detail?.tags.length > 0 && detail?.tags.map((tag, index) => (
                            <DashedChips keyName={index} text={tag} />
                        ))
                    }
                </div>

                <DevStats {...{ detail }} />

                <div className="flex items-center gap-[10px] mt-[20px] bg-[#0090FF] rounded h-[40px] px-[10px] md:w-[300px] lg:w-[60%] lg:mb-[40px]">
                    <Image src={Verified} alt="verified" className="w-[20px] h-[20px]" />
                    <p className="text-white text-[12px] text-center">Skills and seniority verified on {moment(detail?.date_approved).format("MMM DD, YYYY")}</p>
                </div>
            </div>
        </div>
    );
};

const DevStats = ({ detail }) => {
    return (
        <div className="">
            <h3 className="font-semibold text-primary-color mt-[20px] text-[18px] lg:text-[20px]">DevHire Stats</h3>

            <div className="grid grid-cols-2 md:flex md:justify-start md:items-center gap-[10px] mt-[20px]">
                <div className="flex items-center gap-[10px] justify-center flex-col rounded-[10px] bg-black p-[10px] w-[130px] h-[130px]">
                    <p className="text-[30px] font-semibold">{detail?.no_of_projects || 0}</p>
                    <p className="text-[12px] text-[#888888] font-extralight">projects done</p>
                </div>

                <div className="flex items-center gap-[10px] justify-center flex-col rounded-[10px] bg-black p-[10px] w-[130px] h-[130px]">
                    <p className="text-[30px] font-semibold">{detail?.hours_worked || 0}</p>
                    <p className="text-[12px] text-[#888888] font-extralight">hours worked</p>
                </div>

                <div className="flex items-center gap-[10px] justify-center flex-col rounded-[10px] bg-black p-[10px] w-[130px] h-[130px]">
                    <p className="text-[25px] font-semibold">{detail?.open_to_new_offers ? "Open" : "Closed"}</p>
                    <p className="text-[12px] text-[#888888] font-extralight text-center">to new offers</p>
                </div>
            </div>
        </div>
    );
};

export default DeveloperProfile;